<template>
  <div v-show="store.state.messageSendListView" class="contact-block">
    <div class="contact-header">
      <div class="contact-header__title">연락처 정보</div>
      <div class="contact-header__checks">
        <label class="check-chip" :class="{ 'check-chip--on': mailAllCheck }">
          <input v-model="mailAllCheck" type="checkbox" class="check-chip__input" />
          <span>메일 전체</span>
        </label>
        <label class="check-chip" :class="{ 'check-chip--on': smsAllCheck }">
          <input v-model="smsAllCheck" type="checkbox" class="check-chip__input" />
          <span>문자 전체</span>
        </label>
        <div class="checks-divider" />
        <label class="check-chip check-chip--accent" :class="{ 'check-chip--on': allCheck }">
          <input v-model="allCheck" type="checkbox" class="check-chip__input" />
          <span>전체 선택</span>
        </label>
      </div>
    </div>

    <div class="table-scroll">
      <table class="info-table contact-table">
        <thead>
          <tr>
            <th class="table-field" style="width: 120px">성함</th>
            <th class="table-field" style="width: 220px">연락처</th>
            <th class="table-field" style="width: 280px">이메일</th>
            <th class="table-field" style="width: 60px">삭제</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(j, i) in props.contact_info" :key="i">
            <td class="table-data">{{ j?.name }}</td>
            <td class="table-data">
              <div class="contact-cell">
                <span class="mono">{{ j?.tel }}</span>
                <input type="checkbox" v-model="j.telCheck" title="문자 발송 대상" :id="`sms-${i}`" />
              </div>
            </td>
            <td class="table-data">
              <div class="contact-cell">
                <span class="mono">{{ j?.mail }}</span>
                <input type="checkbox" v-model="j.mailCheck" title="메일 발송 대상" :id="`mail-${i}`" />
              </div>
            </td>
            <td class="table-data"></td>
          </tr>
          <!-- 학교 등록 연락처 외에, 이번 발송에만 한 번 쓰고 마는 1회성 수신자.
               "+ 연락처 추가"로 이 자리에 바로 행이 추가된다. 학교 연락처로는 저장되지 않는다. -->
          <tr v-for="(j, i) in extraContacts" :key="`extra-${i}`">
            <td class="table-data">
              <input v-model="j.name" type="text" spellcheck="false" placeholder="이름" class="extra-input" />
            </td>
            <td class="table-data">
              <div class="contact-cell">
                <input v-model="j.tel" type="text" spellcheck="false" placeholder="연락처" class="extra-input mono" />
                <input type="checkbox" v-model="j.telCheck" title="문자 발송 대상" />
              </div>
            </td>
            <td class="table-data">
              <div class="contact-cell">
                <input v-model="j.mail" type="text" spellcheck="false" placeholder="이메일" class="extra-input mono" />
                <input type="checkbox" v-model="j.mailCheck" title="메일 발송 대상" />
              </div>
            </td>
            <td class="table-data">
              <button type="button" class="extra-delete" aria-label="삭제" @click="removeExtraContact(i)">✖</button>
            </td>
          </tr>
          <tr v-if="(!props.contact_info || props.contact_info.length === 0) && extraContacts.length === 0">
            <td class="table-data table-empty" colspan="4">등록된 연락처가 없습니다.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="contact-add-row">
      <button type="button" class="btn-add-extra" @click="addExtraContact">
        + 연락처 추가
      </button>
    </div>

    <div class="preview-grid">
      <!-- 메일 미리보기 -->
      <section class="preview-card">
        <header class="preview-card__header">메일 미리보기</header>
        <div class="preview-mail-meta">
          <div class="mail-subject">
            [알공] {{ name }} 초등학교 계정발급이 완료되었습니다.
          </div>
          <div class="mail-row">
            <span class="mail-row__label">보낸 사람</span>
            <span class="mail-row__value">support@dnsoft.co.kr</span>
          </div>
          <div class="mail-row">
            <span class="mail-row__label">받는 사람</span>
            <div class="mail-row__recipients">
              <span v-if="allContacts.length === 0" class="text-muted">-</span>
              <template v-else>
                <span
                  v-for="(c, index) in allContacts"
                  :key="index"
                >
                  <span v-if="c.mailCheck === true" class="recipient-chip">{{ c.mail }}</span>
                </span>
              </template>
            </div>
          </div>
        </div>
        <div v-html="mailHtml" class="mail-body" />
      </section>

      <!-- 메세지 미리보기 -->
      <section class="preview-card">
        <header class="preview-card__header">문자 미리보기</header>
        <div class="sms-area">
          <textarea
            v-model="smsText"
            class="sms-textarea"
            spellcheck="false"
            placeholder="문자 본문"
          />
          <div class="sms-bubble-meta">
            <div class="tail" />
            <div class="profile">DN</div>
          </div>
        </div>
        <div class="preview-card__actions">
          <button class="btn-primary" @click="onClickSend">
            메일 / 문자 전송하기
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, watch, ref } from "vue";
import { useStore } from "vuex";
import { changeschoolaccountstatus } from "@/api/user-api-list";

const mailHtml = ref("");
const smsText = ref("");
const store = useStore();

const props = defineProps(["code", "name", "contact_info"]);
// eslint-disable-next-line no-unused-vars
const emit = defineEmits(["contactInfo"]);

// 학교에 등록된 기본 연락처(props.contact_info) + 이번 발송에만 쓰는 1회성
// 추가 연락처(extraContacts)를 합쳐서 "전체 선택" 등의 기준으로 삼는다.
const extraContacts = ref([]);
const allContacts = computed(() => [...(props.contact_info || []), ...extraContacts.value]);

const mailAllCheck = computed({
  get: () => allContacts.value.length > 0 && allContacts.value.every((v) => v.mailCheck),
  set: (val) => {
    allContacts.value.forEach((v) => {
      v.mailCheck = val;
    });
  },
});
const smsAllCheck = computed({
  get: () => allContacts.value.length > 0 && allContacts.value.every((v) => v.telCheck),
  set: (val) => {
    allContacts.value.forEach((v) => {
      v.telCheck = val;
    });
  },
});
const allCheck = computed({
  get: () => mailAllCheck.value && smsAllCheck.value,
  set: (val) => {
    mailAllCheck.value = val;
    smsAllCheck.value = val;
  },
});

const addExtraContact = () => {
  extraContacts.value.push({ name: "", tel: "", mail: "", mailCheck: false, telCheck: false });
};
const removeExtraContact = (index) => {
  extraContacts.value.splice(index, 1);
};

const onClickSend = async () => {
  const mailFilter = allContacts.value.filter((v) => v.mailCheck === true);
  const smsFilter = allContacts.value.filter((v) => v.telCheck === true);

  const mail = mailFilter.map((v) => v.mail);
  const tel = smsFilter.map((v) => v.tel);

  // 메일/문자 중 한 채널만 선택해도 발송 가능. 선택한 채널 중 하나라도
  // 발송에 성공하면 계정발급 상태를 '발급완료'로 전이한다.
  // (기존엔 둘 다 선택 + 둘 다 성공해야만 발급완료로 바뀌어, 문자 실패 시
  // 메일이 나갔는데도 정보대기 상태로 남는 문제가 있었음)
  if (mail.length === 0 && tel.length === 0) {
    alert("메일 또는 문자를 1개 이상 선택해주세요.");
  } else {
    const mailData = {
      schoolCode: props.code,
      emailList: mail,
      content: mailHtml.value,
    };

    const smsData = {
      schoolCode: props.code,
      telList: tel,
      content: smsText.value,
    };

    // 선택한 채널만 발송한다
    const mailRes =
      mail.length > 0
        ? await store.dispatch("POST_MAIL_ACCOUNT_ISSUE", mailData)
        : null;
    const smsRes =
      tel.length > 0
        ? await store.dispatch("POST_SMS_ACCOUNT_ISSUE", smsData)
        : null;

    const mailOk = mail.length > 0 && mailRes?.result === true;
    const smsOk = tel.length > 0 && smsRes?.result === true;

    if (mailOk || smsOk) {
      await changeschoolaccountstatus({
        schoolCode: props.code,
        accountStatus: "발급완료",
      });
      const lines = [];
      if (mail.length > 0) lines.push(`- 메일: ${mailOk ? `${mail.length}건 전송 완료` : "전송 실패"}`);
      if (tel.length > 0) lines.push(`- 문자: ${smsOk ? `${tel.length}건 전송 완료` : "전송 실패"}`);
      alert(
        (mailOk && smsOk) || (mail.length === 0) || (tel.length === 0)
          ? `전송이 완료되었습니다.\n\n${lines.join("\n")}`
          : `일부만 전송되었습니다. 실패한 채널은 다시 시도해주세요.\n\n${lines.join("\n")}\n\n(한 채널이 성공하여 계정발급 상태는 '발급완료'로 변경되었습니다)`
      );
      location.reload(true);
    } else {
      alert("메일/문자 전송에 모두 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  }
};

const getMailTemplate = async () => {
  try {
    const result = await store.dispatch("GET_MAIL_TEMPLATE", props.code);
    if (result.result === true) {
      if (result.data === "error") {
        mailHtml.value = '<div style="padding: 20px;">메일 템플릿을 불러오지 못했습니다.</div>';
      } else {
        mailHtml.value = result.data;
      }
    }
  } catch (err) {
    console.log(err);
  }
};
const getSmsTemplate = async () => {
  extraContacts.value = [];

  try {
    const result = await store.dispatch("GET_SMS_TEMPLATE");
    if (result.result === true) {
      if (result.data === "error") {
        smsText.value = "";
      } else {
        smsText.value = result.data;
      }
    }
  } catch (err) {
    console.log(err);
  }
};

watch(() => props.code, getMailTemplate, { immediate: true });
watch(() => props.code, getSmsTemplate, { immediate: true });
</script>

<style scoped>
.contact-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.contact-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.contact-header__title {
  font-size: var(--font-body-strong-size);
  font-weight: var(--font-body-strong-weight);
  color: var(--text-primary);
}

.contact-header__checks {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

/* 연락처/이메일 값 + 발송 대상 체크박스를 같은 셀 안에 나란히 배치 */
.contact-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* "+ 연락처 추가" — 표 아래 가운데 배치 */
.contact-add-row {
  display: flex;
  justify-content: center;
}

.btn-add-extra {
  height: 32px;
  padding: 0 var(--space-4);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--bg-surface);
  color: var(--text-primary);
  font-size: var(--font-small-size);
  font-weight: 600;
  cursor: pointer;
}

.btn-add-extra:hover {
  background: var(--bg-subtle);
}

.extra-input {
  width: 100%;
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-surface-raised);
  font-size: var(--font-body-size);
  color: var(--text-primary);
}

.extra-delete {
  font-size: 14px;
  color: var(--color-error-text);
  cursor: pointer;
  line-height: 1;
  background: none;
  border: none;
}

.check-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--bg-surface-raised);
  font-size: var(--font-small-size);
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.check-chip:hover {
  background: var(--bg-subtle);
  border-color: var(--border-strong);
}

.check-chip--on {
  background: var(--color-primary-50);
  border-color: var(--color-primary-400);
  color: var(--color-primary-700);
}

.check-chip--accent.check-chip--on {
  background: var(--color-primary-100);
  border-color: var(--color-primary-600);
  color: var(--color-primary-800);
  font-weight: 600;
}

.check-chip__input {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: var(--color-primary-600);
  flex-shrink: 0;
}

.checks-divider {
  width: 1px;
  height: 20px;
  background: var(--border-default);
  margin: 0 var(--space-1);
  flex-shrink: 0;
}

.table-scroll {
  overflow-x: auto;
}

.contact-table {
  min-width: 720px;
}

.contact-table .table-data {
  text-align: center;
}

.mono {
  font-family: var(--font-mono-family, "JetBrains Mono", "D2Coding", monospace);
  font-size: var(--font-mono-size);
}

.table-empty {
  color: var(--text-tertiary);
  font-size: var(--font-small-size);
  padding: var(--space-6) !important;
}

.preview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-5);
}

@media (max-width: 1100px) {
  .preview-grid {
    grid-template-columns: 1fr;
  }
}

.preview-card {
  display: flex;
  flex-direction: column;
  background: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.preview-card__header {
  padding: var(--space-3) var(--space-4);
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border-default);
  font-size: var(--font-body-strong-size);
  font-weight: var(--font-body-strong-weight);
  color: var(--text-secondary);
}

.preview-mail-meta {
  padding: var(--space-4);
  border-bottom: 1px solid var(--border-default);
  background: var(--bg-surface);
}

.mail-subject {
  font-size: var(--font-body-strong-size);
  font-weight: var(--font-body-strong-weight);
  color: var(--text-primary);
  margin-bottom: var(--space-3);
}

.mail-row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  font-size: var(--font-small-size);
  color: var(--text-secondary);
  margin-bottom: var(--space-2);
}

.mail-row__label {
  width: 64px;
  flex-shrink: 0;
  color: var(--text-tertiary);
}

.mail-row__value {
  color: var(--text-primary);
}

.mail-row__recipients {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.recipient-chip {
  display: inline-block;
  padding: 2px var(--space-2);
  background: var(--color-primary-50);
  color: var(--color-primary-900);
  border-radius: var(--radius-sm);
  font-size: var(--font-caption-size);
}

.text-muted {
  color: var(--text-tertiary);
}

.mail-body {
  height: 300px;
  overflow: auto;
  background: var(--bg-surface-raised);
  padding: 0;
}

.sms-area {
  position: relative;
  padding: var(--space-4);
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border-default);
  min-height: 320px;
}

.sms-textarea {
  width: 100%;
  height: 260px;
  resize: vertical;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-surface-raised);
  padding: var(--space-3);
  font-size: var(--font-body-size);
  line-height: var(--font-body-line);
  color: var(--text-primary);
  outline: none;
}

.sms-textarea:focus {
  border-color: var(--color-primary-600);
}

.sms-bubble-meta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  justify-content: flex-end;
  margin-top: var(--space-2);
}

.tail {
  width: 12px;
  height: 12px;
  background: var(--bg-subtle);
  clip-path: polygon(0 50%, 100% 0, 100% 100%);
}

.profile {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-caption-size);
  font-weight: 600;
}

.preview-card__actions {
  display: flex;
  justify-content: flex-end;
  padding: var(--space-3) var(--space-4);
  background: var(--bg-surface);
}

.btn-primary {
  height: 36px;
  padding: 0 var(--space-4);
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-body-size);
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:hover {
  background: var(--color-primary-700);
}
</style>
